import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import {
  ARCHIVE_STATUSES,
  ARCHIVE_STATUS_LABEL,
  CARRIERS,
  ORIGINS,
  ORIGIN_FIRST,
  ORIGIN_LABEL,
  SHIPPED_DAYS_SORT,
  archiveStatusOf,
  maySeeDeleted,
  shippedDelay,
  type DateRange,
  type SortPreset,
} from '@/domain';
import { useArchive, useArchiveExport } from '@/application/usecases/useArchive';
import { useTableOrder } from '@/application/usecases/useTableOrder';
import { useTableLayout } from '@/application/usecases/useTableLayout';
import { PAGE_SIZES, useTablePage } from '@/application/usecases/useTablePage';
import { useSession } from '@/application/usecases/useSession';
import type {
  ArchiveRow,
  ArchiveSet,
  ArchiveStatusFilter,
} from '@/application/ports/ArchiveRepository';
import { downloadBlob } from '@/shared/format/download';
import { formatDay } from '@/shared/format/instant';
import { strings } from '@/shared/strings';
import { useMoney } from '@/ui/money/moneyView';
import { Button } from '@/ui/primitives/Button';
import { toast } from '@/ui/primitives/toastStore';
import { Tabs } from '@/ui/primitives/Tabs';
import { CaseTable } from '@/ui/data/CaseTable';
import { FilterBar, type FilterDef } from '@/ui/data/FilterBar';
import type { Column } from '@/ui/data/ResponsiveTable';
import { StatusDot } from '@/ui/data/StatusDot';
import { SortSelect } from '@/ui/data/SortSelect';
import { CASE_SORT_LABEL } from '@/ui/data/casePresets';
import { ArchiveIcon } from '@/ui/icons';
import { SkeletonRows } from '@/ui/feedback/Loading';
import { DeletedCases } from './DeletedCases';
import { ArchivedCases } from './ArchivedCases';
import { SelectionActions } from '@/features/bulk/SelectionActions';
import {
  usePermittedActions,
  type SelectionAction,
} from '@/features/bulk/permittedActions';
import { useSelection } from '@/ui/data/useSelection';
import { useMarkReceived } from '@/features/shipping/useMarkReceived';
import styles from './ArchiveScreen.module.css';

/** Eighty rows is what the prototype shows before asking you to refine. */
const PAGE_SIZE = 80;

/**
 * The archive keeps eighty as its default, which is what the export flow
 * expects and what this screen has always shown, so it is offered beside the
 * four. Section 10.3 of the request of 16 September 2026.
 */
const ARCHIVE_PAGE_SIZES = [...PAGE_SIZES, PAGE_SIZE].sort((a, b) => a - b);

/**
 * Archive. Section 5.2.
 *
 * Two sets, as tabs: the delivered and dispatched cases, which is the archive
 * as it always was and the default, and the deleted ones with the deletions
 * waiting for approval.
 *
 * The Deleted tab is there for whoever may delete a case or decide a deletion,
 * as the access matrix says today, and absent for everybody else: an accountant
 * reading the archive has no business with it. The tab lives in the URL as
 * ?tab=, as the Users screen's do.
 */
export function ArchiveScreen() {
  const session = useSession();
  const [search, setSearch] = useSearchParams();
  const permissions = session.data?.permissions ?? [];
  // The rule the server refuses the Deleted list by, so the tab is never
  // offered to somebody its list would turn away.
  const seesDeleted = maySeeDeleted(permissions);

  const asked = search.get('tab');
  const tab: ArchiveSet =
    asked === 'archived' ? 'archived' : seesDeleted && asked === 'deleted' ? 'deleted' : 'delivered';

  // The badge is the number waiting for somebody's decision, not the whole set.
  const waiting = useArchive(
    { set: 'deleted', deletionStatus: 'pending', pageSize: 1 },
    { enabled: seesDeleted },
  );

  /*
   * The Archived tab is everybody's who reads the archive: an archived case is
   * the lab's business in the way a delivered one is. Section 4 of the request
   * of 24 September 2026.
   */
  return (
    <div>
      <div className={styles.head}>
        <span className={styles.lbl}>{strings.archive.label}</span>
        <h2>{strings.archive.title}</h2>
        <span className={styles.hint}>{strings.archive.hint}</span>
      </div>

      <Tabs
        label={strings.deletion.tabsLabel}
        current={tab}
        onSelect={(key) => setSearch(key === 'delivered' ? {} : { tab: key }, { replace: true })}
        tabs={[
          { key: 'delivered', label: strings.deletion.tabDelivered },
          { key: 'archived', label: strings.archived.tab },
          ...(seesDeleted
            ? [{ key: 'deleted', label: strings.deletion.tabDeleted, badge: waiting.data?.total }]
            : []),
        ]}
      >
        {tab === 'deleted' ? (
          <DeletedCases />
        ) : tab === 'archived' ? (
          <ArchivedCases />
        ) : (
          <DeliveredCases />
        )}
      </Tabs>
    </div>
  );
}

/** A delivered case can be deleted with others, as one can be alone. Section 4 of the request of 24 September 2026. */
const DELIVERED_ACTIONS: readonly SelectionAction[] = ['delete'];
const deliveredKey = (row: ArchiveRow) => row.jobOrderNo;

/** A lab day from an address, or nothing: anything that is not YYYY-MM-DD is no day. */
function dayOf(text: string | null): string {
  return text !== null && /^\d{4}-\d{2}-\d{2}$/.test(text) ? text : '';
}

/**
 * The delivered and dispatched cases.
 *
 * Search plus filters plus sort. The filtering is done by the SERVER, not here:
 * the archive holds every case the lab has ever delivered, and filtering a page
 * of eighty in the browser would search eighty rows rather than the archive.
 *
 * Since the request of 5 October 2026 (third), section 11, every row says one
 * of two things, Shipped or Received: a case is Shipped from the moment it
 * leaves until somebody marks it received. The list filters by the two, shows
 * how many lab days each case has been in Shipped so a delayed delivery stands
 * out, and a case still out is marked received from its row.
 */
function DeliveredCases() {
  const navigate = useNavigate();

  const [q, setQ] = useState('');
  const [status, setStatus] = useState<ArchiveStatusFilter>('all');
  const [origin, setOrigin] = useState('all');
  const [carrier, setCarrier] = useState('all');
  /*
   * The days, kept in the address: `/archive?from=2026-10-01&to=2026-10-07`. The
   * request of 7 October 2026, section 7: the Overview's Completed, On time and
   * Turnaround figures open this list narrowed to the days they were counted
   * over, the narrowing is there to see and to change, and the link can be sent.
   */
  const [address, setAddress] = useSearchParams();
  const delivered = useMemo<DateRange>(
    () => ({ from: dayOf(address.get('from')), to: dayOf(address.get('to')) }),
    [address],
  );
  const setDelivered = (range: DateRange) =>
    setAddress(
      (current) => {
        const next = new URLSearchParams(current);
        for (const key of ['from', 'to'] as const) {
          if (range[key] === '') next.delete(key);
          else next.set(key, range[key]);
        }
        return next;
      },
      { replace: true },
    );

  // The question, the mark and the Undo after it, as on the shipping screen.
  const received = useMarkReceived();

  /*
   * Sorted by the SERVER, as the archive is filtered by the server: it holds
   * every case the lab has ever delivered, and ordering the page in hand would
   * order eighty rows rather than the archive. Recently shipped stays the
   * default, which is the order the screen has always been in.
   */
  const presets = useMemo<SortPreset<ArchiveRow>[]>(
    () => [
      // The key is what a device remembers its choice by, so it keeps its old name.
      { key: 'recentlyDelivered', label: strings.table.recentlyShipped, sortParam: '-deliveredAt' },
      // The cases still out first, the one out longest at the top.
      { key: 'longestInShipped', label: strings.table.longestInShipped, sortParam: SHIPPED_DAYS_SORT },
      { key: 'newestFirst', label: strings.table.newestFirst, sortParam: '-createdAt' },
      { key: 'doctorAtoZ', label: strings.table.doctorAtoZ, sortParam: 'dentist' },
      // One origin's cases first, recently delivered first inside each. Section 2
      // of the request of 24 September 2026.
      ...ORIGINS.map((origin) => ({
        key: ORIGIN_FIRST[origin],
        label: CASE_SORT_LABEL[ORIGIN_FIRST[origin]],
        sortParam: ORIGIN_FIRST[origin],
      })),
    ],
    [],
  );
  const order = useTableOrder('archive', presets);

  /*
   * A page at a time, with the size the reader chose. Section 10 of the request
   * of 16 September 2026 replaces "showing the first 80, refine the search for
   * the rest", which told somebody there were more and then left them to guess
   * how to reach them.
   *
   * Eighty stays the default here, because that is what the export flow expects
   * and what this screen has always shown.
   */
  const paging = useTablePage('archive', PAGE_SIZE, ARCHIVE_PAGE_SIZES);
  // The columns as this reader laid them out, kept on their account. The request of 7 October 2026, section 3.
  const layout = useTableLayout('archive');

  const filter = useMemo(
    () => ({
      q,
      // Asked of the server, and it travels to the export with the rest.
      status,
      origin,
      carrier,
      // An open end is left off the query rather than sent empty.
      from: delivered.from,
      to: delivered.to,
      page: paging.page,
      pageSize: paging.pageSize,
      sort: order.sortParam ?? '-deliveredAt',
    }),
    [q, status, origin, carrier, delivered, paging.page, paging.pageSize, order.sortParam],
  );

  const { data, isPending, isError } = useArchive(filter);
  const exporter = useArchiveExport();

  const rows = data?.items ?? [];

  // Delete, for a selection as for one case. Section 4 of the request of 24 September 2026.
  const actions = usePermittedActions(DELIVERED_ACTIONS);
  const selection = useSelection(deliveredKey);
  const total = data?.total ?? 0;

  // A filter or a new order makes a different list, and page three of the old
  // one is nothing in the new one.
  const { reset } = paging;
  useEffect(() => {
    reset();
  }, [reset, q, status, origin, carrier, delivered, order.presetKey, order.column]);

  const filters: FilterDef[] = [
    {
      kind: 'search',
      label: strings.archive.search,
      placeholder: strings.archive.searchPlaceholder,
      value: q,
      onChange: setQ,
    },
    {
      // Shipped, the cases nobody has marked received, or Received.
      kind: 'select',
      label: strings.archive.status,
      value: status,
      options: [
        { value: 'all', label: strings.archive.allStatuses },
        ...ARCHIVE_STATUSES.map((key) => ({ value: key, label: ARCHIVE_STATUS_LABEL[key] })),
      ],
      onChange: (next) => setStatus(next as ArchiveStatusFilter),
    },
    {
      // In place of the country. Section 1 of the request of 24 September 2026.
      kind: 'select',
      label: strings.archive.origin,
      value: origin,
      options: [
        { value: 'all', label: strings.archive.allOrigins },
        ...ORIGINS.map((key) => ({ value: key, label: ORIGIN_LABEL[key] })),
      ],
      onChange: setOrigin,
    },
    {
      // The day it left the lab, which the wire still calls deliveredAt.
      kind: 'dateRange',
      label: strings.archive.shippedOn,
      from: delivered.from,
      to: delivered.to,
      onChange: setDelivered,
    },
    {
      kind: 'select',
      label: strings.archive.carrier,
      value: carrier,
      options: [
        { value: 'all', label: strings.archive.allCarriers },
        ...CARRIERS.map((name) => ({ value: name, label: name })),
      ],
      onChange: setCarrier,
    },
  ];

  const money = useMoney();
  const columns: Column<ArchiveRow>[] = [
    {
      key: 'product',
      label: strings.archive.item,
      sortable: true,
      render: (row) => row.product,
    },
    {
      key: 'units',
      label: strings.archive.units,
      mono: true,
      align: 'right',
      sortable: true,
      sortValue: (row) => row.units,
      render: (row) => row.units,
    },
    {
      key: 'origin',
      label: strings.archive.origin,
      // The client's order, not the alphabet's, for a header clicked on a page.
      sortable: true,
      sortValue: (row) => ORIGINS.indexOf(row.origin),
      render: (row) => ORIGIN_LABEL[row.origin],
    },
    {
      // The hand-over. The row's `deliveredAt` is the day the case left the lab.
      key: 'deliveredAt',
      label: strings.archive.shippedOn,
      mono: true,
      nowrap: true,
      sortable: true,
      sortValue: (row) => row.deliveredAt,
      // The lab's calendar day, not the UTC one. A case dispatched at two in
      // the morning Dubai time is still yesterday in UTC.
      render: (row) => formatDay(row.deliveredAt),
    },
    {
      /*
       * Beside Shipped on: the day the customer had it, and for a case nobody
       * has marked received, the button that marks it, for whoever may. An
       * accountant kept to their own three screens, who reads the archive and
       * marks nothing, sees the day or an empty cell, as does anybody without
       * `ship`. A button in a row acts on its own and never opens the
       * record. The cell is not mono, because it may hold a button; the day is.
       */
      key: 'deliveredToCustomerAt',
      label: strings.archive.received,
      nowrap: true,
      render: (row) =>
        row.deliveredToCustomerAt !== null ? (
          <span className={styles.day}>{formatDay(row.deliveredToCustomerAt)}</span>
        ) : received.allowed ? (
          <Button
            onClick={() =>
              received.ask({
                jobOrderNo: row.jobOrderNo,
                rowVersion: row.rowVersion,
                dispatchedAt: row.deliveredAt,
              })
            }
          >
            {strings.shipping.markReceived}
          </Button>
        ) : (
          ''
        ),
    },
    {
      /*
       * Whole lab days from the hand-over: running while the case is Shipped,
       * standing still once it is Received. A case still out past the lab's
       * limit reads late, which is what makes a delayed delivery easy to spot;
       * a received one never does, however long it took.
       */
      key: 'daysInShipped',
      label: strings.archive.inShipped,
      mono: true,
      nowrap: true,
      align: 'right',
      sortable: true,
      sortValue: (row) => row.daysInShipped,
      render: (row) => (
        <span
          className={
            shippedDelay(row.daysInShipped, row.deliveredToCustomerAt !== null) === 'late'
              ? styles.late
              : undefined
          }
        >
          {strings.archive.daysInShipped(row.daysInShipped)}
        </span>
      ),
    },
    {
      key: 'value',
      label: strings.archive.value,
      mono: true,
      align: 'right',
      sortable: true,
      sortValue: (row) => row.value ?? 0,
      // Money is absent for a caller without pay or admin, so the column reads
      // as withheld rather than as zero.
      render: (row) =>
        row.value == null ? strings.table.hidden : money(row.value),
    },
    {
      key: 'tracking',
      label: strings.archive.tracking,
      mono: true,
      render: (row) =>
        row.trackingCode === null ? (row.carrier ?? '') : `${row.carrier ?? ''} ${row.trackingCode}`,
    },
    // What the rows carry and the table does not show as it comes. The request of 8 October 2026 (second).
    {
      // The job order that remakes this case, once it has come back.
      key: 'redoneAs',
      label: strings.table.columns.sentBackAs,
      mono: true,
      nowrap: true,
      sortable: true,
      optional: true,
      sortValue: (row) => row.redoneAs ?? '',
      render: (row) => row.redoneAs ?? '',
    },
  ];

  return (
    <>
      <FilterBar
        label={strings.archive.filtersLabel}
        filters={filters}
        sort={<SortSelect presets={presets} value={order.presetKey} onChange={order.choose} />}
        actions={
          <Button
            icon={<ArchiveIcon />}
            disabled={exporter.isPending}
            onClick={() =>
              exporter.mutate(filter, {
                onSuccess: (blob) => {
                  downloadBlob(blob, 'ceramica-archive.csv');
                  toast(`${total} ${strings.archive.exported}`);
                },
                onError: () => toast(strings.archive.exportFailed),
              })
            }
          >
            {exporter.isPending ? strings.archive.exporting : strings.archive.exportCsv}
          </Button>
        }
      />

      {isError && <p className={styles.hint}>{strings.errors.server}</p>}

      {!isError && (
        <>
          {/* What the export will take, which is the filtered set and not the
              page. The page itself is counted under the rows. */}
          <div className={styles.count}>
            <b>{total.toLocaleString()}</b>{' '}
            {total === 1 ? strings.archive.countedOne : strings.archive.counted}
          </div>

          {isPending ? (
            <SkeletonRows rows={6} label={strings.common.loadingArchive} />
          ) : (
            <CaseTable
              customize={layout}
              columns={columns}
              rows={rows}
              selection={actions.length > 0 ? selection : undefined}
              sort={order.column}
              onSortChange={order.sortByColumn}
              // Shipped or Received, the two the list filters by, with the
              // tone its days in Shipped read in. It said where the case got to
              // in the dispatch desk's four words until the request of
              // 5 October 2026 (third), section 11; the carrier and the code
              // are still in the Tracking column.
              status={(row) => (
                <StatusDot
                  tone={shippedDelay(row.daysInShipped, row.deliveredToCustomerAt !== null)}
                >
                  {ARCHIVE_STATUS_LABEL[archiveStatusOf(row.deliveredToCustomerAt)]}
                </StatusDot>
              )}
              statusSortValue={(row) => archiveStatusOf(row.deliveredToCustomerAt)}
              identity={(row) => ({
                jobOrderNo: row.jobOrderNo,
                dentistName: row.dentistName,
                patientName: row.patientName,
              })}
              rowKey={(row) => row.jobOrderNo}
              caption={strings.archive.caption}
              emptyHeading={strings.archive.emptyHeading}
              emptyDetail={strings.archive.emptyDetail}
              onRowClick={(row) => void navigate(`/archive/${row.jobOrderNo}`)}
              paging={{
                page: paging.page,
                pageSize: paging.pageSize,
                total,
                sizes: ARCHIVE_PAGE_SIZES,
                onPage: paging.goTo,
                onPageSize: paging.choose,
                label: strings.table.pagesLabel,
              }}
            />
          )}
        </>
      )}

      {/* After the table, so the bar appearing moves no row under the pointer. */}
      <SelectionActions
        count={selection.count}
        jobOrderNos={selection.rows.map((row) => row.jobOrderNo)}
        assignTarget={{ jobOrderNos: selection.rows.map((row) => row.jobOrderNo) }}
        commonStage={null}
        actions={actions}
        onClear={selection.clear}
        onSelectPage={
          rows.every((row) => selection.selected.has(row.jobOrderNo))
            ? undefined
            : () => selection.onTogglePage(rows, true)
        }
      />

      {received.confirmation}
    </>
  );
}
